import * as RadixDialog from '@radix-ui/react-dialog';
import { useEffect, useRef, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

interface Props {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Required: a dialog without a label is unusable with a screen reader (§9). */
  title: string;
  description?: string;
  children?: ReactNode;
  /** The actions row. A destructive action must NOT be autofocused (§9). */
  footer?: ReactNode;
  trigger?: ReactNode;
}

/**
 * The control `Esc` has to give the focus ring back to.
 *
 * Usually it is simply whatever held focus when the dialog opened. **A menu item is the exception,
 * and it is the common case on §6.5**: the menu closes as the dialog opens, so by the time the
 * dialog closes that item no longer exists and focusing it does nothing — measured, focus landed on
 * `<body>`. A menu's `aria-labelledby` names its trigger (Radix sets it, and ARIA requires the
 * relationship), and the trigger is the control the reader actually came from, so that is the way
 * back.
 */
function openerOf(element: Element | null): HTMLElement | null {
  if (!(element instanceof HTMLElement) || element === document.body) return null;

  const labelledBy = element.closest('[role="menu"]')?.getAttribute('aria-labelledby');
  const trigger =
    labelledBy === null || labelledBy === undefined ? null : document.getElementById(labelledBy);

  return trigger ?? element;
}

/**
 * Confirmations and short forms only — **never the interview form** (§4). Radix handles the focus
 * trap and `Esc`; do not defeat it.
 *
 * `onOpenAutoFocus` is prevented so focus lands on the dialog itself rather than on its first
 * button, which for a destructive confirmation would put "Delete" one Enter away.
 *
 * ⚠️ **Two things below were added by TASK-069's live pass, and each is a measurement rather than a
 * preference. jsdom computes no layout and reports focus the browser does not, so neither could be
 * found by the suite — and neither is verified by the class assertions that guard them.**
 *
 *  1. **`max-h` + `overflow-y-auto`.** The box had neither, and it is `position: fixed`, so content
 *     taller than the window was cut off at both ends with no way to reach it: scrolling the page
 *     does not move a fixed box. Measured in Chrome on the built bundle: §6.4's dialog **with the
 *     duplicate warning showing** is **854 px** tall, so at 1024×600 its top 127 px and its whole
 *     footer sat outside the window — *Cancel*, *Create a separate person* and *Add a new
 *     consideration to the existing card* all unreachable, which is ADR 0002's entire interaction —
 *     and at 390×640 the same. §6.5's *Anonymize* confirmation on an already-anonymised person with
 *     its failure line showing is **694 px**: at 390×640 both *Cancel* and *Anonymize* were off
 *     screen. **The Cancel of an irreversible action must never be unreachable.**
 *  2. **Focus is moved into the dialog on open, and back to the opener on close.** Preventing
 *     Radix's autofocus left focus on the control that opened the dialog — *outside* the content —
 *     and Radix's trap only holds focus that is already inside it. Measured: after opening §6.5's
 *     *Anonymize*, the first three `Tab` presses landed on **Make offer**, **Reject** and **Make
 *     offer** on the card *behind* the modal, and `Esc` dropped focus to `<body>` (§6.4's dialog
 *     dropped it onto a table row) — Sprint 8's drawer defect, one screen over. The content itself
 *     is focused rather than a button, so §9's rule that a destructive confirmation does not
 *     autofocus its destructive action is kept exactly as it was.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  trigger,
}: Props) {
  const contentRef = useRef<HTMLDivElement>(null);
  /** The control the reader came from, and the one `Esc` has to give the focus ring back to. */
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open === false) return undefined;

    /**
     * ⚠️ **A frame late, on purpose.** A dialog opened from a menu item is mounted while the menu is
     * still closing, and the menu restores focus to **its** trigger afterwards — which takes focus
     * back out of the dialog we have just focused, and is also exactly the control the reader came
     * from. So one frame later: if focus is outside the content, remember what holds it and pull
     * focus back in, where Radix's trap can hold it.
     */
    const frame = requestAnimationFrame(() => {
      const content = contentRef.current;
      const active = document.activeElement;
      if (!content || !(active instanceof HTMLElement) || content.contains(active)) return;

      openerRef.current = openerOf(active) ?? openerRef.current;
      content.focus();
    });

    return () => cancelAnimationFrame(frame);
  }, [open]);

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>}
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 bg-ink-900/20" />
        <RadixDialog.Content
          ref={contentRef}
          // Focusable programmatically, never by Tab: the dialog is where focus lands on open, and
          // it must not become an extra stop in the cycle once the reader is inside.
          tabIndex={-1}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            openerRef.current = openerOf(document.activeElement) ?? openerRef.current;
            contentRef.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            const opener = openerRef.current;
            // Only when it is still on the page: a menu item that opened this dialog is long gone,
            // and Radix's own restore is the better answer there than focusing nothing.
            if (!opener?.isConnected) return;
            event.preventDefault();
            opener.focus();
          }}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2',
            // Additive and defaulted: a dialog whose content fits is laid out exactly as before —
            // `auto` shows no scrollbar until there is something to scroll.
            'max-h-[calc(100dvh-2rem)] overflow-y-auto',
            'rounded border border-rule bg-paper p-6 shadow-overlay',
          )}
        >
          <RadixDialog.Title className="text-[15px] font-semibold text-ink-900">
            {title}
          </RadixDialog.Title>
          {description && (
            <RadixDialog.Description className="mt-2 text-sm text-ink-700">
              {description}
            </RadixDialog.Description>
          )}
          {children && <div className="mt-4">{children}</div>}
          {footer && <div className="mt-6 flex justify-end gap-2">{footer}</div>}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
